// @flow
import * as React from 'react'
import is from 'mz-utils/is'
import nextId from 'mz-utils/nextId'
import { eventToKey } from '../../utils'

// Types
type Props = {
  id?: string,
  showed?: boolean,
  onClose?: () => any,
  render: (boolean, Function) => React.Node,
}
type State = {
  showed?: boolean,
}

/**
 * A component to catch various global events to close the lightbox.
 * For now it catch press to "Esc" button and press to android "back"
 * button.
 * @param  {Object} props
 * @return {React.Element}
 */
class VisibilityController extends React.Component<Props, State> {
  haveHistory: boolean

  lightboxId: string

  state = {}

  static contextTypes = {
    lightboxHistory: () => null,
  }

  constructor(props: Props) {
    super(props)
    if (is.undef(props.showed)) {
      this.lightboxId = is.undef(props.id)
        ? `lb${nextId()}`
        : `${props.id || ''}`
    }
  }

  get history(): ?Object {
    return this.lightboxId ? this.context.lightboxHistory : null
  }

  get showed(): boolean {
    const { showed: propsShowed } = this.props
    const { showed: stateShowed } = this.state

    return !!(
      (!this.history && is.notUndef(propsShowed) && propsShowed) ||
      (!this.history && is.undef(propsShowed) && stateShowed) ||
      (this.history && this.history.isShowed(this.lightboxId))
    )
  }

  open = () => {
    if (this.showed) return Promise.resolve()
    if (this.history) {
      return this.history.open(this.lightboxId)
    }
    return is.undef(this.props.showed)
      ? new Promise<any>((r) => this.setState({ showed: true }, r))
      : Promise.resolve()
  }

  close = () => {
    if (!this.showed) return Promise.resolve()
    if (this.history) {
      return this.history.close(this.lightboxId)
    }

    let result = Promise.resolve()
    if (is.undef(this.props.showed)) {
      result = new Promise<any>((r) => this.setState({ showed: false }, r))
    }

    const { onClose } = this.props
    onClose && onClose()
    return result
  }

  componentDidMount() {
    this.history &&
      this.history.watch(this.lightboxId, this.handleHistoryChange)
    document.addEventListener('keyup', this.handleEscapePress, false)
  }

  componentWillUnmount() {
    if (this.history) {
      this.showed && this.history.close(this.lightboxId)
      this.history.unwatch(this.lightboxId, this.handleHistoryChange)
    }
    document.removeEventListener('keyup', this.handleEscapePress, false)
  }

  handleHistoryChange = (closed: boolean) => {
    this.forceUpdate()
    if (closed) {
      const { onClose } = this.props
      onClose && onClose()
    }
  }

  handleEscapePress = (event: Object) => {
    const id = this.lightboxId
    const key = eventToKey(event)

    if (
      key === 'Esc' &&
      this.showed &&
      this.history &&
      this.history.isTop(id)
    ) {
      this.close()
    }
  }

  render() {
    return this.props.render(this.showed, this.close)
  }
}

export default VisibilityController
